import React, { type FC, type JSX, useEffect } from 'react';
import {
  type JourneyContractBaggage,
  type JourneyContractSegment,
  JourneyContractSegmentTransportType,
  type JourneyContractWay,
  JourneyContractWayTransportCategory,
  type TransportData,
} from '@/domainLogic/index.ts';
import { airportMarkerSelection } from '@/app/components/search/components/SearchPageContainer/MapContainer/utils.ts';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import clickEvent from '@/app/components/search/Curiosity/clickEvent.ts';
import { RouteType } from '@/app/components/search/Curiosity/index.ts';
import DebugBlock from '@/app/components/search/components/Card/Debug/DebugBlock/index.tsx';
import { DebugInfoContainer } from '@/app/components/search/components/Banners/Debug/styled.ts';
import type { TransportLogosCompany } from '@lmn/lmn-components/components/Trip/TransportSummary/components/transportLogos/TransportLogosTypes.d.ts';
import Flight from './Transport';
import { TransportDetailsContainer } from './styled';
import type { MixedWayInfo } from './utils';
import { isMixedWay } from './utils';
import TransportSlim from './TransportSlim';
import { useIsLgOrGreater } from '../../hooks/useMediaSSR.ts';
import { changeDateFormat } from '../../common/dates.ts';
import { getTransportLogosCompany } from '../utils.tsx';

export type TransportProps = {
  isOutBound?: boolean;
  departureDate: string;
  departureAirportCode: string;
  departureAirportName: string;
  departureCity: string;
  arrivalDate: string;
  arrivalAirportCode: string;
  arrivalAirportName: string;
  arrivalCity: string;
  numberOfStops: number;
  durationInMinutes: number;
  locale: string;
  isWaitingState: boolean;
  dealsCount: number;
  baggage: JourneyContractBaggage;
  differentDepartureAirportCodes: boolean;
  differentArrivalAirportCodes: boolean;
  dictionary: Dictionary;
  companies: TransportLogosCompany[];
  isMixedWay?: MixedWayInfo;
  numberOfBaggagePiecesIncluded?: number;
};

export type TransportDetailsProps = {
  dictionary: Dictionary;
  isWaitingState: boolean;
  transportData: TransportData;
  handleTransportClicked: (id: string) => void;
  index: number;
  itemsLength: number;
  expand: boolean;
  setExpand: React.Dispatch<React.SetStateAction<boolean>>;
  showDebug: boolean;
  dealsCount: number;
  numberOfBaggagePiecesIncluded?: number;
};

const TransportDetails: FC<TransportDetailsProps> = ({
  dictionary,
  isWaitingState,
  transportData,
  handleTransportClicked,
  itemsLength,
  expand,
  setExpand,
  showDebug,
  dealsCount,
  numberOfBaggagePiecesIncluded,
}): JSX.Element => {
  const { localization, curiosityParams, commonData } = usePageWrapperData();

  const isMobile = !useIsLgOrGreater();
  const { locale } = localization;
  const { outbound, inbound } = transportData;

  const outBoundSegments = outbound.segments;
  const outBoundSegmentsLength = outBoundSegments.length - 1;
  const inBoundSegments = inbound.segments;
  const inBoundSegmentsLength = inBoundSegments.length - 1;
  const arrivalAirportCode = outBoundSegments[outBoundSegmentsLength].arrival.hub.id;
  const debugIsVisible = showDebug && expand;

  const handleOnClick = () => {
    if (isWaitingState) return;

    if (itemsLength > 1 && !expand) {
      setExpand(true);
      clickEvent({
        clickParams: {
          event_name: 'flights_expand',
          event_subcontext: 'preselected_flight',
        },
        routeType: RouteType.OSMAP,
        commonData,
        localization,
        curiosityParams,
      });
    } else {
      handleTransportClicked(transportData.transportId);
      clickEvent({
        clickParams: {
          event_name: 'journey_dialog_open',
          event_subcontext: 'preselected_flight',
        },
        routeType: RouteType.OSMAP,
        commonData,
        localization,
        curiosityParams,
      });
    }
  };

  useEffect(() => {
    if (isWaitingState) {
      setExpand(false);
    }
  }, [isWaitingState, setExpand]);

  const handleOnMouseEnter = (): void => {
    if (!isMobile) {
      airportMarkerSelection(arrivalAirportCode, true);
    }
  };

  const handleOnMouseLeave = (): void => {
    if (!isMobile) {
      airportMarkerSelection(arrivalAirportCode, false);
    }
  };

  const differentDepartureAirportCodes =
    outBoundSegments[0].departure.hub.id !== inBoundSegments[inBoundSegmentsLength].arrival.hub.id;

  const differentArrivalAirportCodes =
    outBoundSegments[outBoundSegmentsLength].arrival.hub.id !== inBoundSegments[0].departure.hub.id;

  const outboundProps: TransportProps = {
    isOutBound: true,
    dictionary,
    departureDate: outBoundSegments?.[0]?.departure?.datetime,
    departureAirportCode: outBoundSegments?.[0]?.departure?.hub?.id,
    departureAirportName: outBoundSegments?.[0]?.departure?.hub?.name,
    departureCity: outbound?.locations?.origin,
    arrivalDate: outBoundSegments?.[outBoundSegmentsLength]?.arrival?.datetime,
    arrivalAirportCode: outBoundSegments?.[outBoundSegmentsLength]?.arrival?.hub?.id,
    arrivalAirportName: outBoundSegments?.[outBoundSegmentsLength]?.arrival?.hub?.name,
    arrivalCity: outbound?.locations?.destination,
    baggage: outbound?.baggage,
    numberOfStops: outBoundSegmentsLength,
    durationInMinutes: outbound?.durationInMinutes,
    locale,
    isWaitingState,
    dealsCount,
    differentDepartureAirportCodes,
    differentArrivalAirportCodes,
    companies: getTransportLogosCompany(outbound?.segments),
    isMixedWay: isMixedWay(outbound?.segments),
    numberOfBaggagePiecesIncluded,
  };

  const inboundProps: TransportProps = {
    isOutBound: false,
    dictionary,
    departureDate: inBoundSegments?.[0]?.departure?.datetime,
    departureAirportCode: inBoundSegments?.[0]?.departure?.hub?.id,
    departureAirportName: inBoundSegments?.[0]?.departure?.hub?.name,
    departureCity: inbound?.locations?.origin,
    arrivalDate: inBoundSegments?.[inBoundSegmentsLength]?.arrival?.datetime,
    arrivalAirportCode: inBoundSegments?.[inBoundSegmentsLength]?.arrival?.hub?.id,
    arrivalAirportName: inBoundSegments?.[inBoundSegmentsLength]?.arrival?.hub?.name,
    arrivalCity: inbound?.locations?.destination,
    baggage: inbound?.baggage,
    numberOfStops: inBoundSegmentsLength,
    durationInMinutes: inbound?.durationInMinutes,
    locale,
    isWaitingState,
    dealsCount,
    differentDepartureAirportCodes,
    differentArrivalAirportCodes,
    companies: getTransportLogosCompany(inbound?.segments),
    isMixedWay: isMixedWay(inbound?.segments),
    numberOfBaggagePiecesIncluded,
  };

  const getLayout = () => {
    if (isMobile) {
      return (
        <TransportDetailsContainer
          onClick={handleOnClick}
          onMouseEnter={handleOnMouseEnter}
          onMouseLeave={handleOnMouseLeave}
          $desktop={!isMobile}
        >
          <TransportSlim {...outboundProps} />
          <TransportSlim {...inboundProps} />
        </TransportDetailsContainer>
      );
    }

    return (
      <TransportDetailsContainer
        onClick={handleOnClick}
        onMouseEnter={handleOnMouseEnter}
        onMouseLeave={handleOnMouseLeave}
        $desktop={!isMobile}
      >
        <Flight {...outboundProps} />
        <Flight {...inboundProps} />
      </TransportDetailsContainer>
    );
  };

  return (
    <>
      {getLayout()}
      {debugIsVisible && (
        <DebugInfoContainer style={{ marginTop: '-4px' }}>
          <DebugBlock data={[{ value: transportData.tripId }]} showSeparator={false} />
        </DebugInfoContainer>
      )}
    </>
  );
};

export const adaptTransport = ({
  transportId,
  inbound,
  outbound,
}: {
  transportId: string;
  inbound: TransportBoundType;
  outbound: TransportBoundType;
}): TransportData => ({
  inbound: adaptBound(inbound),
  outbound: adaptBound(outbound),
  tripId: transportId,
  transportId,
  score: 0,
  flightCost: 0,
  flightType: '',
});

export const adaptBound = (bound: TransportBoundType): JourneyContractWay => ({
  segments: bound.segments.map(adaptSegment),
  transportCategory: JourneyContractWayTransportCategory.STANDARD,
  durationInMinutes: bound.timeIntervalInMinutes,
  selected: true,
  baggage: {
    hand: bound.handBaggageIncluded,
    hold: bound.includeBaggage,
  },
  locations: {
    origin: bound.segments[0].departureHub.cityName || bound.origin,
    destination: bound.segments[bound.segments.length - 1].arrivalHub.cityName || bound.destination,
  },
});

const adaptSegment = (segment: TransportSegment): JourneyContractSegment => ({
  transport: {
    class: segment.classAvailability,
    operativeId: segment.transportNumber,
    companyCode: segment.company.code,
    companyName: segment.company.name,
    type:
      segment.type === 'TRAIN' ? JourneyContractSegmentTransportType.TRAIN : JourneyContractSegmentTransportType.FLIGHT,
  },
  departure: {
    hub: {
      id: segment.departureHub.id,
      name: segment.departureHub.name,
    },
    datetime: changeDateFormat(segment.departureDate, `dd/MM/yy'#'HH:mm`, `yyyy-MM-dd'T'HH:mm:ss.SSS`),
  },
  arrival: {
    hub: {
      id: segment.arrivalHub.id,
      name: segment.arrivalHub.name,
    },
    datetime: changeDateFormat(segment.arrivalDate, `dd/MM/yy'#'HH:mm`, `yyyy-MM-dd'T'HH:mm:ss.SSS`),
  },
});

export default TransportDetails;
